/**
 * Service worker registration.
 *
 * Runs once in production builds (CRA sets NODE_ENV automatically). In dev the
 * SW is intentionally skipped to avoid stale caches confusing hot reload.
 *
 * Update strategy (Phase 64.1):
 *   1. When a new SW finishes installing, send SKIP_WAITING immediately so it
 *      activates without forcing the user to close every tab.
 *   2. Listen for `controllerchange` and reload the page exactly once — the
 *      next paint serves the fresh bundle. No more "close 3-4 times to
 *      stabilise" loops.
 *   3. If the user happens to be in the middle of a swipe / interaction, we
 *      defer the reload until the next time the app is foregrounded.
 */

const SW_URL = "/service-worker.js";

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  if (
    process.env.NODE_ENV !== "production" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  ) {
    return;
  }

  let pendingReload = false;
  const tryReload = () => {
    if (!pendingReload) return;
    // Only reload when the tab is actually visible so we never yank the page
    // out from under a user mid-swipe.
    if (document.visibilityState !== "visible") return;
    pendingReload = false;
    window.location.reload();
  };

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(SW_URL)
      .then((reg) => {
        const promoteWaiting = (worker) => {
          if (!worker) return;
          // Tell the new SW to skip waiting and activate immediately.
          worker.postMessage({ type: "SKIP_WAITING" });
          window.dispatchEvent(
            new CustomEvent("nowcast:sw-update", { detail: { reg } }),
          );
        };

        // 1. If there's already a waiting worker (e.g. user visits after the
        //    update was downloaded in a previous session), kick it now.
        if (reg.waiting && navigator.serviceWorker.controller) {
          promoteWaiting(reg.waiting);
        }

        // 2. Watch for a fresh update during this session.
        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              promoteWaiting(newWorker);
            }
          });
        });

        // 3. Periodically check for updates while the app is open. Means the
        //    user doesn't have to close + relaunch to discover a new version.
        const tick = () => {
          if (document.visibilityState === "visible") {
            reg.update().catch(() => {});
          }
        };
        setInterval(tick, 60 * 60 * 1000); // hourly while open
        document.addEventListener("visibilitychange", tick);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("SW registration failed:", err);
      });

    // Reload the page when a new SW takes over.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      pendingReload = true;
      tryReload();
    });
    document.addEventListener("visibilitychange", tryReload);
  });
}
